import {
  redactServerMessage,
  resolveServerErrorCopy,
  UserFacingError,
  type ServerErrorBody,
} from '@/shared/domain'
import {
  HttpError,
  isTransportFailure,
  readServerErrorBodyMessage,
  readServerErrorCode,
  readServerFieldErrors,
} from '@/shared/infra/http'
import { classifyErrorSeverity, type ReportSeverity } from './classify-error-severity'
import { getPersonaSnapshot } from './persona-snapshot'

export const TRANSPORT_FAILURE_CONTEXT_KEY = 'transportFailure'

export type ReportErrorOptions = {
  fallbackMessage?: string
  context?: Record<string, unknown>
}

export interface ErrorReporter {
  captureException(err: unknown, context?: Record<string, unknown>): void
  captureMessage(message: string, level: ReportSeverity, context?: Record<string, unknown>): void
  setUser(id: string | null): void
}

class ConsoleErrorReporter implements ErrorReporter {
  captureException(err: unknown): void {
    console.error(err)
  }

  captureMessage(message: string, level: ReportSeverity, context?: Record<string, unknown>): void {
    console.warn(message, context)
  }

  setUser(): void {}
}

let activeReporter: ErrorReporter = new ConsoleErrorReporter()

export function setErrorReporter(reporter: ErrorReporter): void {
  activeReporter = reporter
}

export function setUserIdentity(id: string | null): void {
  activeReporter.setUser(id)
}

function isUserFacing(err: unknown): err is Error {
  return err instanceof UserFacingError
}

function buildContext(
  err: unknown,
  base?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const merged: Record<string, unknown> = { ...(base ?? {}) }

  const persona = getPersonaSnapshot()
  if (persona) {
    merged.personaOrgId = persona.orgId
    merged.personaOrgType = persona.orgType
    merged.personaRole = persona.role
  }

  if (isTransportFailure(err)) {
    merged[TRANSPORT_FAILURE_CONTEXT_KEY] = true
  }

  if (err instanceof HttpError) {
    merged.httpStatus = err.status
    if (err.url) merged.httpUrl = err.url
    if (err.method) merged.httpMethod = err.method
    if (err.body !== undefined) merged.httpResponseBody = err.body
  }

  return Object.keys(merged).length === 0 ? undefined : merged
}

function describeError(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

const GENERIC_FALLBACK_MESSAGE = 'Something went wrong. Please try again.'
const GENERIC_VALIDATION_MESSAGE = 'Validation failed'
const FIELD_MESSAGE_LIMIT = 3

function readErrorBody(err: HttpError): ServerErrorBody | null {
  const body = err.body
  return body !== null && typeof body === 'object' ? (body as ServerErrorBody) : null
}

function prefersFieldErrors(serverMessage: string | null): boolean {
  if (serverMessage === null) return true
  const trimmed = serverMessage.trim()
  return trimmed.length === 0 || trimmed === GENERIC_VALIDATION_MESSAGE
}

function joinFieldMessages(err: HttpError): string | null {
  const messages: string[] = []
  for (const fieldError of readServerFieldErrors(err)) {
    const redacted = redactServerMessage(fieldError.message)
    if (redacted !== null) messages.push(redacted)
  }
  if (messages.length === 0) return null
  const shown = messages.slice(0, FIELD_MESSAGE_LIMIT)
  const remaining = messages.length - shown.length
  const joined = shown.join('; ')
  return remaining === 0 ? joined : `${joined} and ${remaining} more`
}

function resolveUserMessage(err: unknown, fallbackMessage: string): string {
  if (!(err instanceof HttpError)) return fallbackMessage
  if (err.status < 400 || err.status >= 500) return fallbackMessage

  const copy = resolveServerErrorCopy(readServerErrorCode(err), readErrorBody(err))
  if (copy !== null) return copy

  const serverMessage = readServerErrorBodyMessage(err)
  if (prefersFieldErrors(serverMessage)) {
    const fieldMessages = joinFieldMessages(err)
    if (fieldMessages !== null) return fieldMessages
  }

  return redactServerMessage(serverMessage) ?? fallbackMessage
}

export function reportError(err: unknown, options: ReportErrorOptions = {}): string {
  if (isUserFacing(err)) {
    return err.message
  }
  const context = buildContext(err, options.context)
  if (classifyErrorSeverity(err) === 'warning') {
    activeReporter.captureMessage(describeError(err), 'warning', context)
  } else {
    activeReporter.captureException(err, context)
  }
  return resolveUserMessage(err, options.fallbackMessage ?? GENERIC_FALLBACK_MESSAGE)
}
